import { useUser } from 'containers/user/hooks/useUser'
import { useAddLike, useLikes, useRemoveLike } from './hooks/useLikes'
import { SurfaceType } from './types/likes'
import { useEffect, useMemo, useRef, useState } from 'react'
import { userHasLiked } from './misc/likes'
import { ColoredHeart, EmptyHeart } from '@/public/assets/v2/images/icons'
import { AnimateContainer } from '@/components/commons/container'
import { Text } from '@/components/commons/typography/Text'
import { LikesProfiles } from './components/LikesProfiles'
import { Modal } from '@/components/modal/Modal'
import { ProfileCardLike } from './components/ProfileCardLike'

interface LikesProps {
  surfaceType: SurfaceType
  referencedId: string
  isProjectPage?: boolean
}

const heartSize = {
  width: 20,
  height: 20,
}

export const Likes = ({
  surfaceType,
  referencedId,
  isProjectPage,
}: LikesProps) => {
  const [currentPage, setCurrentPage] = useState(0)
  const containerRef = useRef<HTMLDivElement>(null)
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [likeThis, setLikeThis] = useState(false)
  const { user } = useUser()
  const { mutateAsync: addLike } = useAddLike()
  const { mutateAsync: removeLike } = useRemoveLike()
  const { data, isLoading } = useLikes({
    surfaceType,
    referencedId,
    page: currentPage,
  })

  const hasLiked = useMemo(() => {
    return userHasLiked(data?.likes, user?.id)
  }, [data, user])

  const onLike = () => {
    if (!likeThis && !hasLiked) {
      addLike({
        surfaceType,
        referencedId,
      })
      setLikeThis(true)
    } else {
      removeLike({
        surfaceType,
        referencedId,
      })
      setLikeThis(false)
    }
  }

  useEffect(() => {
    setLikeThis(hasLiked)
  }, [hasLiked])

  const likesCount = useMemo(() => {
    if (hasLiked) {
      return likeThis ? data?.numberOfResults : data?.numberOfResults - 1
    }

    return likeThis ? data?.numberOfResults + 1 : data?.numberOfResults
  }, [data, likeThis, hasLiked])

  const likesProfiles = useMemo(() => {
    const likesArrayList = []

    if (data?.likes) {
      likesArrayList[data.page] = data.likes
    }

    return likesArrayList
  }, [data])

  const addNextPageCallback = (indexList: number, indexLikes: number) => {
    // If the last element of the list is reached, load the next page
    if (
      indexList === likesProfiles.length - 1 &&
      indexLikes === likesProfiles[indexList].length - 1
    ) {
      return callNextPage
    }

    return undefined
  }

  const callNextPage = () => {
    // Count the number of total likes from the list
    const totalLikes = likesProfiles.reduce(
      (acc, likes) => acc + likes.length,
      0
    )
    // Total Likes in db
    const totalLikesDB = data?.numberOfResults

    // If the total likes in the list is less than the total likes in the db, load the next page
    if (totalLikes < totalLikesDB) {
      setCurrentPage(currentPage + 1)
    }
  }

  const openModal = () => {
    if (likesCount > 0) {
      setIsModalOpen(true)
    }
  }

  return (
    <>
      <Modal
        className="--v2 full-w p-0 z-50 relative"
        size="xl"
        centered
        overlayProps={{
          bg: 'rgba(0, 0, 0, 0.06)',
          blur: 2,
        }}
        opened={isModalOpen}
        onClose={() => setIsModalOpen(false)}
      >
        <div
          className="flex flex-column gap-12 relative"
          style={{ height: '100%' }}
          ref={containerRef}
        >
          {likesProfiles.map((likes, index) => (
            <>
              {likes?.map((like, indexLikes) => (
                <ProfileCardLike
                  key={like?.id}
                  onIntersect={addNextPageCallback(index, indexLikes)}
                  id={like.userWhoLikedId}
                />
              ))}
            </>
          ))}
        </div>
      </Modal>

      <div
        className="py-14 px-20 flex justify-space-between"
        style={{
          opacity: isLoading ? 0 : 1,
        }}
      >
        <div>
          <LikesProfiles
            likes={data?.likes || []}
            totalLikes={likesCount}
            isProjectPage={isProjectPage}
          />
        </div>

        <div className="flex items-center gap-10">
          <div
            onClick={onLike}
            className="relative cursor-pointer"
            style={heartSize}
          >
            <EmptyHeart
              className="text-primary absolute z-2 top-0 left-0"
              style={heartSize}
            />

            {likeThis && (
              <AnimateContainer
                type="bounceIn"
                active={likeThis}
                setOpacityNone
              >
                <ColoredHeart
                  className="text-primary absolute z-20 top-0 left-0 ani"
                  style={heartSize}
                />
              </AnimateContainer>
            )}
          </div>

          <div onClick={openModal}>
            <Text className="cursor-pointer">{likesCount}</Text>
          </div>
        </div>
      </div>
    </>
  )
}
